/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, EyeOff, ShapeCircle, AlertCheckCircleFill } from '@jda/lui-common-icon-library-mui5';
import { Button, Box, Typography, TextField, InputAdornment, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { useStyles } from './styles';
import Loader from '../../shared/Loader';
import FormDialog from '../../shared/Dialog/FormDialog';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { resetUserDirectoryPassword } from '../../services/user';
import interpolateString from '../../utility/stringInterpolation';
import { LABELS, MESSAGES, PASSWORD_POLICY_ARRAY } from './constant';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { getAllPasswordPolicy, getPasswordPolicyRule } from '../../services/setting';
import passwordDictionary from '../../shared/passwordDictionary/passwordDictionary.json';
import { LuiModal } from '@jda/lui-common-component-library-mui5';

const ResetPassword: React.FC<any> = ({ userName, userIdp, userId }) => {
  const { classes } = useStyles();
  const appLocation = useSelector(selectAppLocation);
  const dispatch = useDispatch();

  const [userPasswordText, setUserPasswordText] = useState('');
  const [showDialogLoader, setShowDialogLoader] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [isMinMax, setIsMinMax] = useState(false);
  const [isIncludeNumbers, setIsIncludeNumbers] = useState(false);
  const [isLettersInMixedCase, setIsLettersInMixedCase] = useState(false);
  const [isIncludeSpecialCharacters, setIsIncludeSpecialCharacters] = useState(false);
  const [isMaxConsecutiveLetters, setIsMaxConsecutiveLetters] = useState(false);
  const [displayResetPassword, setDispalyResetPassword] = useState(false);
  const [showResetPasswordConfirmation, setShowResetPasswordConfirmation] = useState(false);
  const [forceResetPassword, setForceResetPassword] = useState(false);
  const [defaultPasswordPolicyRule, setDefaultPasswordPolicyRule] = React.useState<any>(null);
  const [isValid, setIsValid] = useState(false);
  const [isNotCommonPassword, setIsNotCommonPassword] = useState(false);
  const commonPasswordsSet = new Set(passwordDictionary);

  const getDefaultPasswordPolicyRule = useCallback(async () => {
    if (userIdp) {
      const defaultPasswordPolicy = await getAllPasswordPolicy().then((res: any) => {
        const idpPolicy = res.find((policy: any) => policy.idpsNames.length > 0);
        const defaultPolicy = res.find((policy: any) => policy.isDefault);
        return idpPolicy || defaultPolicy;
      });
      if (defaultPasswordPolicy?.id) {
        const defaultPasswordPolicyRule = await getPasswordPolicyRule(
          defaultPasswordPolicy?.id,
          appLocation?.name
        );
        setDefaultPasswordPolicyRule(defaultPasswordPolicyRule);
      }
    }
  }, [userIdp, appLocation]);
  useEffect(() => {
    getDefaultPasswordPolicyRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseResetPassword = () => {
    setDispalyResetPassword(false);
    setShowResetPasswordConfirmation(false);
  };

  const updatePassword = (value: any) => {
    const tempState = {
      isMinMax: true,
      isIncludeNumbers: true,
      isIncludeSpecialCharacters: true,
      isLettersInMixedCase: true,
      isMaxConsecutiveLetters: true,
      isNotCommonPassword: true,
    };

    if (defaultPasswordPolicyRule.minLength && defaultPasswordPolicyRule.maxLength) {
      tempState.isMinMax = value.length >= defaultPasswordPolicyRule.minLength && value.length <= defaultPasswordPolicyRule.maxLength;
    }

    if (defaultPasswordPolicyRule.includeNumbers) {
      tempState.isIncludeNumbers = /[0-9]/.test(value);
    }

    if (defaultPasswordPolicyRule.includeSpecialCharacters) {
      tempState.isIncludeSpecialCharacters = /[!@#$%^&*]/.test(value);
    }

    if (defaultPasswordPolicyRule.lettersInMixedCase) {
      tempState.isLettersInMixedCase = /^(?=.*[a-z])(?=.*[A-Z])/.test(value);
    }

    if (defaultPasswordPolicyRule.maxConsecutiveLetters > 0) {
      tempState.isMaxConsecutiveLetters = !new RegExp(`(.)\\1{${defaultPasswordPolicyRule.maxConsecutiveLetters},}`).test(value);
    }

    if (defaultPasswordPolicyRule.excludePasswordsInDictionary) {
      tempState.isNotCommonPassword = !commonPasswordsSet.has(value);
    }

    setIsMinMax(tempState.isMinMax);
    setIsIncludeNumbers(tempState.isIncludeNumbers);
    setIsIncludeSpecialCharacters(tempState.isIncludeSpecialCharacters);
    setIsLettersInMixedCase(tempState.isLettersInMixedCase);
    setIsMaxConsecutiveLetters(tempState.isMaxConsecutiveLetters);
    setIsNotCommonPassword(tempState.isNotCommonPassword);

    const allValid = Object.values(tempState).every(Boolean);
    setIsValid(allValid)
    setUserPasswordText(value);
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };

  const handleSaveResetPassword = () => {
    const payload = {
      idpType: userIdp.idpType,
      userId: userId,
      password: userPasswordText,
      forceResetPassword
    };
    if (userPasswordText === '' || !isValid) {
      displaySnackbar(interpolateString([userName], MESSAGES.PASSWORD_REQUIRED), 'error');
      return;
    }
    setShowDialogLoader(true);
    resetUserDirectoryPassword(payload, appLocation?.name)
      .then(response => {
        if (response) {
          setShowDialogLoader(false);
          setShowResetPasswordConfirmation(false);
          displaySnackbar(interpolateString([userName], MESSAGES.PASSWORD_RESET_SUCCESS), 'success');
          setDispalyResetPassword(false);
          setShowResetPasswordConfirmation(false);
        }
      })
      .catch((_error: any) => {
        setShowDialogLoader(false);
        const errorMessage = `${MESSAGES.PASSWORD_RESET_FAILURE} ${_error?.response?.data?.message}`;
        displaySnackbar(interpolateString([userName], errorMessage), 'error');
        setShowResetPasswordConfirmation(false);
      });
  };

  const handleResetPassword = () => {
    setDispalyResetPassword(true);
    setUserPasswordText('');
  };
  const handleForceResetPassword = () => {
    setForceResetPassword(!forceResetPassword);
  }
  return (
    <Box>
      {showResetPasswordConfirmation && (
        <AlertDialog
          recordName={userName}
          showDialogLoader={false}
          hideCanNotBeUndoneMessage={true}
          handleOnOk={handleResetPassword}
          data-testid="reset-password-alert"
          secondButtonText={LABELS.BUTTON_CANCEL}
          dialogContentText={LABELS.RESET_PASSWORD_TITLE}
          primaryButtonText={LABELS.BUTTON_RESET_PASSWORD}
          handleOnCancel={() => setShowResetPasswordConfirmation(false)}
          dialogTitle={interpolateString([userName], MESSAGES.RESET_PASSWORD_CONFIRMATION)}
        />
      )}
      <LuiModal
        modalSize="sm"
        isOpen={displayResetPassword}
        data-testid={'copy-password-dialog'}
        modalTitle={LABELS.RESET_PASSWORD_TITLE}
        handleClose={() => handleCloseResetPassword()}
        primaryAction={
          <Button
            color="primary"
            disableElevation
            variant="outlined"
            id="resetPasswordCloseBtn"
            name="resetPasswordCloseBtn"
            onClick={handleCloseResetPassword}
            data-testid={'reset-password-close-btn'}
          >
            {LABELS.BUTTON_CLOSE}
          </Button>
        }
        secondaryAction={
          <Button
            color="primary"
            disableElevation
            variant="contained"
            id="resetPasswordSaveBtn"
            name="resetPasswordSaveBtn"
            onClick={handleSaveResetPassword}
            data-testid={'reset-password-save-btn'}
          >
            {LABELS.BUTTON_SAVE}
          </Button>
        }
      >
        <Typography gutterBottom component="p" variant="body1" id="userPasswordText" data-testid="userPasswordText">
          {`Reset Password for ${userName}, Passwords are required to conform to the defined password policy.`}
        </Typography>
        <Box className={classes.resetPasswordWrap}>
          <TextField
            id="password"
            name="password"
            fullWidth
            label={LABELS.PASSWORD}
            data-testid={'password'}
            value={userPasswordText}
            variant="standard"
            error={userPasswordText !== '' && !isValid}
            inputProps={{ maxLength: 50 }}
            className={classes.resetPasswordInput}
            helperText={(userPasswordText !== '' && !isValid) ? MESSAGES.PASSWORD_REQUIRED : ''}
            type={showUserPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    id="show-hide-password-button"
                    data-testid={'show-hide-password-button'}
                    aria-label={showUserPassword ? LABELS.HIDE_PASSWORD : LABELS.SHOW_PASSWORD}
                    onClick={() => {
                      setShowUserPassword(showPassword => !showPassword);
                    }}
                  >
                    {showUserPassword ? <EyeOff fontSize="small" /> : <Eye fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            onChange={(e: any) => updatePassword(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={forceResetPassword} onChange={handleForceResetPassword} size='small'/>}
            label={LABELS.FORCE_RESET_PASSWORD}
          />
          {defaultPasswordPolicyRule && userPasswordText && (
            <Box>
              {defaultPasswordPolicyRule?.minLength && defaultPasswordPolicyRule?.maxLength && (
                <Box className={classes.checkBoxWrap}>
                  {isMinMax ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                  }
                  <Typography variant='caption' className={classes.policyText}>
                    {interpolateString(
                      [defaultPasswordPolicyRule.minLength, defaultPasswordPolicyRule.maxLength],
                      PASSWORD_POLICY_ARRAY['minMax']
                    )}
                  </Typography>
                </Box>
              )}
              {defaultPasswordPolicyRule?.includeNumbers && (
                <Box className={classes.checkBoxWrap}>
                  {isIncludeNumbers ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                  }
                  <Typography variant='caption' className={classes.policyText}>{PASSWORD_POLICY_ARRAY['includeNumbers']}</Typography>
                </Box>
              )}
              {defaultPasswordPolicyRule?.includeSpecialCharacters && (
                <Box className={classes.checkBoxWrap}>
                  {isIncludeSpecialCharacters ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }} ></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }} ></ShapeCircle>
                  }
                  <Typography variant='caption' className={classes.policyText}>
                    {PASSWORD_POLICY_ARRAY['includeSpecialCharacters']}
                  </Typography>
                </Box>
              )}
              {defaultPasswordPolicyRule?.lettersInMixedCase && (
                <Box className={classes.checkBoxWrap}>
                  {isLettersInMixedCase ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }} ></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>
                  }
                  <Typography variant='caption' className={classes.policyText}>
                    {PASSWORD_POLICY_ARRAY['lettersInMixedCase']}
                  </Typography>
                </Box>
              )}
              {defaultPasswordPolicyRule?.maxConsecutiveLetters && (
                <Box className={classes.checkBoxWrap}>
                  {isMaxConsecutiveLetters ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }} ></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }} ></ShapeCircle>}
                  <Typography variant='caption' className={classes.policyText}>
                    {interpolateString(
                      [defaultPasswordPolicyRule.maxConsecutiveLetters],
                      PASSWORD_POLICY_ARRAY['maxConsecutiveLetters']
                    )}
                  </Typography>
                </Box>
              )}
              {defaultPasswordPolicyRule?.excludePasswordsInDictionary && (
                <Box className={classes.checkBoxWrap}>
                  {isNotCommonPassword ?
                    <AlertCheckCircleFill style={{ color: LABELS.VALID_COLOR, fontSize: 15 }}></AlertCheckCircleFill>
                    :
                    <ShapeCircle style={{ color: LABELS.DEFAULT_COLOR, fontSize: 15 }}></ShapeCircle>}
                  <Typography variant='caption' className={classes.policyText}>
                    {PASSWORD_POLICY_ARRAY['commonPassword']}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Loader showLoader={showDialogLoader} isCenter customClasses={classes.loaderStyle} />
      </LuiModal>

      <Button
        color="primary"
        variant="outlined"
        data-testid={'reset-password-button'}
        className={classes.resetPasswordButton}
        onClick={() => setShowResetPasswordConfirmation(true)}
      >
        {LABELS.RESET_PASSWORD_TITLE}
      </Button>
    </Box>
  );
};

export default ResetPassword;
